import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Card,
  Row,
  Col,
  Container,
  Alert,
  Modal,
  Badge,
  Spinner,
  ListGroup,
} from "react-bootstrap";
import {
  CheckCircle,
  Shield,
  CreditCard,
  AlertCircle,
  Zap,
  Clock,
  Info as InfoIcon,
  AlertTriangle,
} from "react-feather";
import Swal from "sweetalert2";

import UpiAtmService from "../../../services/UpiAtmService";
import SideBar from "../../../components/Sidebar";
import LoadingScreen from "../../../components/LoadingScreen/LoadingScreen";
import SubscriptionDialog from "../../../components/Dialogs/SubscriptionDialog";
import { useSubscriptionCheck } from "../../../hooks/useSubscriptionCheck";
import { CheckCircleFill, Info, QrCodeScan } from "react-bootstrap-icons";
import QRCode from "react-qr-code";

const UPICashPoint = () => {
  const navigate = useNavigate();

  // Form states
  const [mobile, setMobile] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const [generatingQR, setGeneratingQR] = useState(false);

  // Location states (silent)
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // QR and transaction states
  const [qrData, setQrData] = useState(null);
  const [orderId, setOrderId] = useState("");
  const [qrExpiryDate, setQrExpiryDate] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState("PENDING");
  const [txnData, setTxnData] = useState(null);

  // Onboarding states
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardMessage, setOnboardMessage] = useState("");
  const [merchantStatus, setMerchantStatus] = useState(null);
  const [jioEkycStatus, setJioEkycStatus] = useState(null);
  const [isOnboarded, setIsOnboarded] = useState(false);

  // State to track if QR is generated (to hide notes)
  const [isQrGenerated, setIsQrGenerated] = useState(false);

  const quickAmounts = [500, 1000, 2000, 5000];

  const pollingRef = useRef(null);
  const expiryTimerRef = useRef(null);

  const { premiumStatus, showDialog, setShowDialog, checkSubscriptionStatus } =
    useSubscriptionCheck();

  // Helper: Check if amount is in multiples of 100
  const isMultipleOf100 = (value) => {
    return Number(value) % 100 === 0;
  };

  // Convert amount to words
  const numberToWords = (num) => {
    if (!num || num === 0) return "Zero";

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];

    const convert = (n) => {
      if (n < 20) return ones[n];
      if (n < 100)
        return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      if (n < 1000)
        return (
          ones[Math.floor(n / 100)] +
          " Hundred" +
          (n % 100 ? " " + convert(n % 100) : "")
        );
      if (n < 100000)
        return (
          convert(Math.floor(n / 1000)) +
          " Thousand" +
          (n % 1000 ? " " + convert(n % 1000) : "")
        );
      if (n < 10000000)
        return (
          convert(Math.floor(n / 100000)) +
          " Lakh" +
          (n % 100000 ? " " + convert(n % 100000) : "")
        );
      return (
        convert(Math.floor(n / 10000000)) +
        " Crore" +
        (n % 10000000 ? " " + convert(n % 10000000) : "")
      );
    };

    return convert(num) + " Rupees Only";
  };

  // Helper: check if form is valid
  const isFormValid = useCallback(() => {
    const isMobileValid =
      mobile && mobile.length === 10 && /^\d{10}$/.test(mobile);
    const isAmountValid =
      amount &&
      Number(amount) >= 100 &&
      Number(amount) <= 5000 &&
      isMultipleOf100(amount);
    return isMobileValid && isAmountValid;
  }, [mobile, amount]);

  // Get remaining seconds from expiry date
  const getRemainingSecondsFromExpiry = (expiryDateStr) => {
    if (!expiryDateStr) return 0;
    const expiry = new Date(expiryDateStr).getTime();
    const now = new Date().getTime();
    return Math.max(0, Math.floor((expiry - now) / 1000));
  };

  // Request location silently (no UI alerts, just resolve/reject)
  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Geolocation not supported"));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          let errorMsg = "Location access failed. ";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg =
                "Location permission denied. Please enable location access.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = "Location unavailable. Check GPS.";
              break;
            case error.TIMEOUT:
              errorMsg = "Location request timed out.";
              break;
            default:
              errorMsg += error.message;
          }
          reject(new Error(errorMsg));
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      );
    });
  }, []);

  // Onboarding check
  const checkLocalStorageOnboarding = () => {
    const storedMerchant = localStorage.getItem("jio_merchant_status");
    const storedEkyc = localStorage.getItem("jio_ekyc_status");
    if (storedMerchant === "REGISTERED" && storedEkyc === "VERIFIED") {
      setIsOnboarded(true);
      return true;
    }
    return false;
  };

  const fetchMerchantOnboardingStatus = useCallback(async () => {
    if (checkLocalStorageOnboarding()) return;
    try {
      const result = await UpiAtmService.ekycStatus();
      if (!result.success) {
        Swal.fire("Oops!", result.message || "Request failed!", "error");
        return;
      }
      const res = result.data;
      if (res.status === "SUCCESS") {
        const respData = res.data;
        const mStatus = respData?.merchant_status;
        const ekycStatus = respData?.ekyc_status;
        setMerchantStatus(mStatus);
        setJioEkycStatus(ekycStatus);
        localStorage.setItem("jio_merchant_status", mStatus);
        localStorage.setItem("jio_ekyc_status", ekycStatus);
        if (mStatus === "REGISTERED" && ekycStatus === "VERIFIED") {
          setIsOnboarded(true);
        } else {
          setIsOnboarded(false);
          let msg = "";
          if (mStatus !== "REGISTERED")
            msg += "Agent is not registered with Bank. ";
          if (ekycStatus !== "VERIFIED") msg += "JIO e-KYC is not verified.";
          setOnboardMessage(msg);
          setShowOnboardModal(true);
        }
      } else {
        Swal.fire("Oops!", res.message || "Something went wrong", "error");
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || error.message || "Network error";
      Swal.fire("Oops!", errorMsg, "error");
    }
  }, []);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        await checkSubscriptionStatus();
        await fetchMerchantOnboardingStatus();
        // Pre-fetch location silently (don't block UI)
        getCurrentLocation().catch(() => {});
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [
    checkSubscriptionStatus,
    fetchMerchantOnboardingStatus,
    getCurrentLocation,
  ]);

  // QR expiry countdown
  useEffect(() => {
    if (!qrExpiryDate || !qrData) return;
    const updateRemaining = () => {
      const remaining = getRemainingSecondsFromExpiry(qrExpiryDate);
      setRemainingSeconds(remaining);
      if (remaining <= 0) {
        if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
        if (pollingRef.current) clearInterval(pollingRef.current);
        setQrData(null);
        setOrderId("");
        setQrExpiryDate(null);
        setPaymentStatus("EXPIRED");
        setIsQrGenerated(false);
        Swal.fire({
          icon: "warning",
          title: "QR Code Expired",
          text: "The QR code has expired. Please generate a new one.",
          timer: 3000,
          showConfirmButton: false,
        });
      }
    };
    updateRemaining();
    expiryTimerRef.current = setInterval(updateRemaining, 1000);
    return () => clearInterval(expiryTimerRef.current);
  }, [qrExpiryDate, qrData]);

  // Poll transaction status
  useEffect(() => {
    if (!orderId || !qrData) return;
    const pollStatus = async () => {
      try {
        const result = await UpiAtmService.qrStatus(orderId);

        if (!result.success) return;

        const txn = result.data.data;
        const status = (txn.txn_status || "").toUpperCase();
        setPaymentStatus(status);
        if (status === "SUCCESS" || status === "FAILED") {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setTxnData(txn);
          setQrData(null);
          setQrExpiryDate(null);
          setIsQrGenerated(false);

          setOrderId("");
          setPaymentStatus(status);

          if (status === "SUCCESS") {
            Swal.fire({
              icon: "success",
              title: "Payment Successful",
              text: "Cash withdrawal completed successfully.",
            });
          } else {
            Swal.fire({
              icon: "error",
              title: "Transaction Failed",
              text: txn.txn_description || "Transaction failed",
            });
          }
        }
      } catch (error) {
        console.log(error);
      }
    };
    pollStatus();
    pollingRef.current = setInterval(pollStatus, 5000);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [orderId, qrData]);

  const generateQrHandler = async () => {
    if (!isFormValid()) {
      if (!isMultipleOf100(amount)) {
        Swal.fire(
          "Oops!",
          "Please enter amount in multiples of ₹100",
          "warning",
        );
      } else {
        Swal.fire(
          "Oops!",
          "Please enter valid 10-digit mobile and amount (₹100-₹5000)",
          "warning",
        );
      }
      return;
    }
    if (premiumStatus !== "true") {
      setShowDialog(true);
      return;
    }
    if (!isOnboarded) {
      setShowOnboardModal(true);
      return;
    }

    // Get location if not already available
    let currentLat = latitude;
    let currentLng = longitude;
    if (!currentLat || !currentLng) {
      try {
        const location = await getCurrentLocation();
        currentLat = location.lat;
        currentLng = location.lng;
      } catch (err) {
        Swal.fire({
          icon: "error",
          title: "Location Required",
          text:
            err.message ||
            "Unable to get your location. Please enable location access.",
        });
        return;
      }
    }

    setGeneratingQR(true);
    try {
      const result = await UpiAtmService.generateQR(
        mobile,
        amount,
        currentLat,
        currentLng,
      );
      if (!result.success) {
        Swal.fire("Oops!", result.message, "error");
        return;
      }
      const res = result.data;

      if (res.status == "SUCCESS") {
        const data = res.data;

        setQrData(data.upi_qr);
        setOrderId(data.order_id);
        setQrExpiryDate(data.upi_qr.expiry_date);
        setRemainingSeconds(Number(data.upi_qr.expiry_second || 0));
        setPaymentStatus("PENDING");
        setTxnData(null);
        setIsQrGenerated(true); // Set QR generated to true
      } else {
        Swal.fire("Oops!", res.message, "error");
      }
    } catch (error) {
      Swal.fire(
        "Oops!",
        error.response?.data?.message || error.message,
        "error",
      );
    } finally {
      setGeneratingQR(false);
    }
  };

  const resetTransaction = () => {
    setTxnData(null);
    setQrData(null);
    setOrderId("");
    setQrExpiryDate(null);
    setRemainingSeconds(0);
    setPaymentStatus("PENDING");
    setMobile("");
    setAmount("");
    setIsQrGenerated(false);
  };

  const handleGoToOnboarding = () => {
    setShowOnboardModal(false);
    navigate("/bc-agent-onboarding/jio-ekyc");
  };

  // Important Notes Component (White color, bullet points)
  const ImportantNotes = () => (
    <Card
      className="shadow-sm border-0"
      style={{ borderRadius: "12px", backgroundColor: "#ffffff" }}
    >
      <Card.Header className="bg-white border-bottom">
        <h6 className="mb-0 d-flex align-items-center gap-2 text-dark">
          <InfoIcon size={16} /> Important Notes
        </h6>
      </Card.Header>
      <Card.Body className="p-3">
        <div
          className="d-flex align-items-center gap-3 mb-2"
          style={{ fontSize: "13px" }}
        >
          <span>
            • <strong>Min Withdrawal:</strong> ₹100
          </span>
          <span>
            • <strong>Max Withdrawal:</strong> ₹5,000
          </span>
        </div>
        <div className="mb-2" style={{ fontSize: "13px" }}>
          • <strong>Daily Limit per Customer:</strong> ₹10,000
        </div>
        <div style={{ fontSize: "13px" }}>
          • <strong>Time Gap Required:</strong> Minimum 30 minutes gap between 2
          transactions of same customer
        </div>
      </Card.Body>
    </Card>
  );

  // Supported UPI Apps Component
  const SupportedUpiApps = () => (
    <div className="mt-3">
      <small className="text-muted d-block mb-2">Supported UPI Apps</small>
      <div className="d-flex flex-wrap gap-2 justify-content-start">
        <img
          src="/assets/images/google-pay-logo.png"
          height={28}
          alt="Google Pay"
          onError={(e) => (e.target.style.display = "none")}
        />
        <img
          src="/assets/images/phonepe-logo.png"
          height={28}
          alt="PhonePe"
          onError={(e) => (e.target.style.display = "none")}
        />
        <img
          src="/assets/images/paytm-logo.png"
          height={28}
          alt="Paytm"
          onError={(e) => (e.target.style.display = "none")}
        />
        <img
          src="/assets/images/amazon-pay-logo.png"
          height={28}
          alt="Amazon Pay"
          onError={(e) => (e.target.style.display = "none")}
        />
        <img
          src="/assets/images/whatsapp-pay-logo.png"
          height={28}
          alt="WhatsApp Pay"
          onError={(e) => (e.target.style.display = "none")}
        />
        <img
          src="/assets/images/bhim-logo.png"
          height={28}
          alt="BHIM"
          onError={(e) => (e.target.style.display = "none")}
        />
      </div>
    </div>
  );

  const statusConfig = {
    SUCCESS: {
      title: "Cash Withdrawal Successful",
      icon: <CheckCircleFill size={40} />,
      banner: "linear-gradient(135deg,#16a34a,#22c55e)",
      badge: "success",
      alertVariant: "success",
      amountClass: "text-success",
      message:
        "Cash withdrawal has been successfully completed and cash can now be handed over to the customer.",
    },

    FAILED: {
      title: "Cash Withdrawal Failed",
      icon: <AlertCircle size={40} />,
      banner: "linear-gradient(135deg,#dc2626,#ef4444)",
      badge: "danger",
      alertVariant: "danger",
      amountClass: "text-danger",
      message: "Transaction failed. Do not hand over cash to the customer.",
    },

    EXPIRED: {
      title: "Transaction Expired",
      icon: <Clock size={40} />,
      banner: "linear-gradient(135deg,#d97706,#f59e0b)",
      badge: "warning",
      alertVariant: "warning",
      amountClass: "text-warning",
      message:
        "QR expired before payment completion. Please create a new transaction.",
    },
  };

  const currentStatus = statusConfig[paymentStatus];

  return (
    <Container fluid className="mt-3">
      {loading && <LoadingScreen />}

      <Row>
        <SideBar />

        <Col md={9}>
          <Card className="shadow-sm p-4 mb-4" style={{ borderRadius: "12px" }}>
            {/* Header with logos on right side */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="d-flex align-items-center">
                <div className="bg-primary p-2 rounded-circle me-3">
                  <QrCodeScan size={24} color="white" />
                </div>
                <div>
                  <h5 className="mb-1">UPI Cash Point</h5>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <img
                  src="/assets/images/jio-bank-logo.png"
                  height={30}
                  alt="Jio Bank"
                />
                <span className="text-muted">|</span>
                <img
                  src="/assets/images/upi-cash-point-logo.png"
                  height={30}
                  alt="UPI Cash Point"
                />
              </div>
            </div>

            <Row className="justify-content-center">
              {/* Before QR: Show form and notes side by side */}
              {!isQrGenerated && !txnData && (
                <>
                  <Col lg={8}>
                    <Card className="shadow-sm">
                      <Card.Body>
                        <Alert
                          variant="warning"
                          className="d-flex align-items-center"
                        >
                          <AlertCircle size={18} className="me-2" />
                          <small>
                            QR Expires in <b>30 seconds</b>. Customer can make
                            payment within <b>45 seconds.</b>.
                          </small>
                        </Alert>

                        <Form.Group className="mb-3">
                          <Form.Label>Customer Number</Form.Label>
                          <Form.Control
                            value={mobile}
                            maxLength={10}
                            onChange={(e) =>
                              setMobile(e.target.value.replace(/\D/g, ""))
                            }
                            isInvalid={
                              mobile.length > 0 && !/^\d{10}$/.test(mobile)
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            Enter valid 10-digit number
                          </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-3">
                          <Form.Label>Amount To Withdraw (₹)</Form.Label>
                          <Form.Control
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            isInvalid={
                              amount &&
                              (Number(amount) < 100 ||
                                Number(amount) > 5000 ||
                                !isMultipleOf100(amount))
                            }
                            placeholder="Enter amount in multiples of 100"
                          />
                          <Form.Text className="text-muted">
                            Amount must be in multiples of ₹100 (Min: ₹100, Max:
                            ₹5,000)
                          </Form.Text>
                          <Form.Control.Feedback type="invalid">
                            {amount && !isMultipleOf100(amount)
                              ? "Amount must be in multiples of ₹100"
                              : "Amount must be ₹100-₹5000"}
                          </Form.Control.Feedback>
                        </Form.Group>

                        <div className="d-flex flex-wrap gap-2 mb-4">
                          {quickAmounts.map((amt) => (
                            <Button
                              key={amt}
                              variant={
                                Number(amount) === amt
                                  ? "primary"
                                  : "outline-primary"
                              }
                              onClick={() => setAmount(amt)}
                            >
                              ₹{amt}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="success"
                          className="w-100"
                          onClick={generateQrHandler}
                          disabled={!isFormValid() || generatingQR}
                        >
                          {generatingQR ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                className="me-2"
                              />
                              Generating...
                            </>
                          ) : (
                            <>
                              <Zap size={18} className="me-2" />
                              Generate QR
                            </>
                          )}
                        </Button>

                        <Card className="border-0 bg-light mt-3">
                          <Card.Body>
                            <h6 className="fw-bold mb-3">Supported UPI Apps</h6>

                            <div className="d-flex flex-wrap gap-2">
                              <Badge bg="primary">Google Pay</Badge>
                              <Badge bg="primary">Paytm</Badge>
                              <Badge bg="primary">BHIM</Badge>
                              <Badge bg="primary">CRED</Badge>
                              <Badge bg="primary">Jupiter</Badge>
                              <Badge bg="primary">Groww</Badge>
                              <Badge bg="primary">Navi</Badge>
                              <Badge bg="primary">IDFC Bank App</Badge>
                              <Badge bg="primary">CUB Bank App</Badge>
                              <Badge bg="primary">Indusind Bank App</Badge>
                              <Badge bg="primary">Yes Bank App</Badge>
                              <Badge bg="primary">Paynearby Sathi App</Badge>
                            </div>

                            <small className="text-muted d-block mt-3">
                              Supported by 25+ banks including SBI, HDFC, ICICI,
                              Axis Bank, Kotak, Jio Payments Bank, NSDL Payments
                              Bank and more.
                            </small>
                          </Card.Body>
                        </Card>
                      </Card.Body>
                    </Card>
                  </Col>

                  <Col lg={4}>
                    <ImportantNotes />
                  </Col>
                </>
              )}

              {isQrGenerated && qrData && !txnData && (
                <Card className="">
                  <Card.Body className="p-4">
                    <Row className="align-items-center">
                      {/* LEFT SIDE */}
                      <Col lg={6}>
                        <h6 className="fw-bold mb-2">Withdraw Cash via UPI</h6>

                        <p className="text-muted mb-4">
                          Ask the customer to scan the QR using any UPI app and
                          complete the payment.
                        </p>

                        {/* Amount Card */}
                        <Card className="border-success bg-success bg-opacity-10 mb-3">
                          <Card.Body className="text-center">
                            <small className="text-muted">
                              Withdrawal Amount
                            </small>

                            <h1
                              className="fw-bold text-success mb-1"
                              style={{ fontSize: "1.5rem" }}
                            >
                              ₹{amount}
                            </h1>

                            <small className="text-muted">
                              {numberToWords(Number(amount))}
                            </small>
                          </Card.Body>
                        </Card>

                        {/* Timer */}
                        <div className="mb-3">
                          <Badge
                            bg={
                              remainingSeconds > 20
                                ? "success"
                                : remainingSeconds > 10
                                  ? "warning"
                                  : "danger"
                            }
                            className="px-3 py-2 fs-6"
                          >
                            <Clock size={15} className="me-2" />
                            Expires in {remainingSeconds}s
                          </Badge>

                          <div className="mt-2">
                            <small className="text-muted">
                              Payment must be completed within 45 seconds.
                            </small>
                          </div>
                        </div>

                        <Alert
                          variant="warning"
                          className="d-flex align-items-center"
                        >
                          <AlertTriangle size={18} className="me-2" />
                          <small>
                            Do not refresh or close this page while the payment
                            is being processed.
                          </small>
                        </Alert>

                        <Card className="border-0 bg-light">
                          <Card.Body>
                            <h6 className="fw-bold mb-3">Supported UPI Apps</h6>

                            <div className="d-flex flex-wrap gap-2">
                              <Badge bg="primary">Google Pay</Badge>
                              <Badge bg="primary">Paytm</Badge>
                              <Badge bg="primary">BHIM</Badge>
                              <Badge bg="primary">CRED</Badge>
                              <Badge bg="primary">Jupiter</Badge>
                              <Badge bg="primary">Groww</Badge>
                              <Badge bg="primary">Navi</Badge>
                              <Badge bg="primary">IDFC Bank App</Badge>
                              <Badge bg="primary">CUB Bank App</Badge>
                              <Badge bg="primary">Indusind Bank App</Badge>
                              <Badge bg="primary">Yes Bank App</Badge>
                              <Badge bg="primary">Paynearby Sathi App</Badge>
                            </div>

                            <small className="text-muted d-block mt-3">
                              Supported by 25+ banks including SBI, HDFC, ICICI,
                              Axis Bank, Kotak, Jio Payments Bank, NSDL Payments
                              Bank and more.
                            </small>
                          </Card.Body>
                        </Card>
                      </Col>

                      {/* RIGHT SIDE */}
                      <Col lg={6}>
                        <div className="text-center">
                          <h6 className="fw-bold mt-3">Scan QR to Pay</h6>

                          <p className="text-muted mb-4">
                            Open any UPI app and scan this QR code
                          </p>

                          <div
                            className="mx-auto bg-white p-4 shadow-sm"
                            style={{
                              width: "fit-content",
                              borderRadius: "20px",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <QRCode value={qrData.qr_string} size={220} />
                          </div>

                          <div className="mt-3 mb-3">
                            <Spinner
                              animation="border"
                              variant="primary"
                              size="sm"
                              className="me-2"
                            />
                            <span className="fw-semibold text-primary">
                              Waiting for customer payment...
                            </span>
                          </div>

                          {/* Powered By */}
                          <div className="border-top pt-3">
                            <small className="text-muted fw-semibold d-block mb-2">
                              Powered By
                            </small>

                            <div className="d-flex justify-content-center align-items-center gap-4">
                              <img
                                src="/assets/images/jio-bank-logo.png"
                                alt="Jio Bank"
                                height="30"
                              />

                              <img
                                src="/assets/images/upi-cash-point-logo.png"
                                alt="UPI Cash Point"
                                height="30"
                              />
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
              {/* Success Transaction View */}
              {txnData &&
                ["SUCCESS", "FAILED", "EXPIRED"].includes(paymentStatus) && (
                  <Col lg={10} className="mx-auto">
                    <Card className=" ">
                      {/* Success Banner */}
                      <div
                        className="text-center text-white py-3"
                        style={{
                          background: currentStatus?.banner,
                        }}
                      >
                        {currentStatus.icon}

                        <h5 className="fw-bold mt-2 mb-0">
                          {currentStatus.title}
                        </h5>
                        <p>{txnData?.txn_description}</p>
                      </div>
                      {/* Amount */}
                      <Card.Body>
                        <div className="text-center mb-3">
                          <div
                            className={`fw-bold ${currentStatus.amountClass}`}
                            style={{
                              fontSize: "2rem",
                              lineHeight: 1,
                            }}
                          >
                            ₹{txnData.amount}
                          </div>

                          <small className="text-muted">Amount Withdrawn</small>
                        </div>

                        <Row>
                          {/* Customer */}
                          <Col md={6}>
                            <Card className="h-100 border">
                              <Card.Header className="bg-light fw-bold">
                                Customer Details
                              </Card.Header>

                              <Card.Body className="py-3 px-3">
                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">Name</span>
                                  <strong>{txnData.payer?.name}</strong>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">Mobile</span>
                                  <strong>{txnData.mobile}</strong>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">VPA</span>
                                  <strong>{txnData.payer?.vpa}</strong>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">Bank</span>
                                  <strong>{txnData.payer?.bank_name}</strong>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>

                          {/* Transaction */}
                          <Col md={6}>
                            <Card className="h-100 border">
                              <Card.Header className="bg-light fw-bold">
                                Transaction Details
                              </Card.Header>

                              <Card.Body className="py-3 px-3">
                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">RRN</span>
                                  <strong>{txnData.rrn}</strong>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">Order ID</span>
                                  <strong>{txnData.order_id}</strong>
                                </div>

                                <div className="d-flex justify-content-between mb-2">
                                  <span className="text-muted">Status</span>
                                  <Badge bg={currentStatus.badge}>
                                    {paymentStatus}
                                  </Badge>
                                </div>

                                <div className="d-flex justify-content-between">
                                  <span className="text-muted">
                                    Date & Time
                                  </span>
                                  <strong>{txnData.timestamp}</strong>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>

                        {/* Footer Message */}
                        <Alert
                          variant={currentStatus.alertVariant}
                          className="mt-4 mb-0"
                        >
                          {currentStatus.message}
                        </Alert>

                        {/* Buttons */}
                        <Row className="mt-4">
                          {paymentStatus === "SUCCESS" && (
                            <Col md={6}>
                              <Button
                                variant="outline-primary"
                                className="w-100"
                              >
                                Print Receipt
                              </Button>
                            </Col>
                          )}

                          <Col md={paymentStatus === "SUCCESS" ? 6 : 12}>
                            <Button
                              variant={currentStatus.alertVariant}
                              className="w-100"
                              onClick={resetTransaction}
                            >
                              New Transaction
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Onboarding Required Modal */}
      <Modal
        show={showOnboardModal}
        onHide={() => setShowOnboardModal(false)}
        centered
        backdrop="static"
        size="md"
      >
        <Modal.Header className="bg-danger text-white border-0">
          <Modal.Title className="d-flex align-items-center">
            <AlertCircle size={28} className="me-2" />
            Onboarding Required
          </Modal.Title>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowOnboardModal(false)}
            aria-label="Close"
          />
        </Modal.Header>
        <Modal.Body className="pt-4">
          <div className="text-center mb-3">
            <div className="bg-warning bg-opacity-10 rounded-circle p-3 d-inline-flex mb-3">
              <Shield size={48} className="text-warning" />
            </div>
            <h5 className="mb-2">Access Restricted</h5>
            <p className="text-muted mb-3">
              You need to complete onboarding before using UPI Cash Point.
            </p>
          </div>
          <Alert variant="danger" className="mb-3">
            <div className="d-flex">
              <AlertCircle size={18} className="me-2 flex-shrink-0" />
              <div>
                <strong>Missing Requirements:</strong>
                <ul className="mb-0 mt-1 ps-3">
                  {merchantStatus !== "REGISTERED" && (
                    <li>Merchant is not onboarded with bank</li>
                  )}
                  {jioEkycStatus !== "VERIFIED" && (
                    <li>JIO e-KYC not verified</li>
                  )}
                </ul>
              </div>
            </div>
          </Alert>
          <div className="bg-light p-3 rounded">
            <div className="d-flex align-items-start">
              <Info size={18} className="text-primary me-2 mt-0.5" />
              <small className="text-muted">
                Once your onboarding is approved and e-KYC is verified, you'll
                be able to:
                <ul className="mt-1 mb-0">
                  <li>UPI Cash Withdrawal service</li>
                </ul>
              </small>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center gap-3">
          <Button
            variant="light"
            onClick={() => setShowOnboardModal(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleGoToOnboarding}
            className="px-4"
          >
            Complete Onboarding
          </Button>
        </Modal.Footer>
      </Modal>

      <SubscriptionDialog
        show={showDialog}
        onHide={() => setShowDialog(false)}
        serviceName="UPI Cash Withdrawal"
      />
    </Container>
  );
};

export default UPICashPoint;
